import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate, formatCurrency, getErrorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge, SourceBadge } from '../components/StatusBadge';
import { SLAWidget } from '../components/SLAWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, UserPlus, Play, Pause, CheckCircle, Lock, MessageSquare, Send, 
  DollarSign, ClipboardCheck, AlertCircle, Calendar, Package, Plus, Copy, 
  MoreVertical, FileText, Paperclip, CircleDot, Clock, User, ChevronRight,
  MessageCircle, XCircle, RotateCcw
} from 'lucide-react';
import FileUpload from '../components/FileUpload';
import VoicePlayer from '../components/VoicePlayer';
import { toast } from 'sonner';

export default function WorkOrderDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  const [wo, setWo] = useState(null);
  const [comments, setComments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState('');
  const [resolution, setResolution] = useState('');
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [costs, setCosts] = useState({ cost_labor: '', cost_parts: '', cost_vendor: '' });
  
  // Timeline state
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState('all');
  
  // Action confirmation modal
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, status: null });
  
  // Close modal optional fields
  const [closeDowntimeMinutes, setCloseDowntimeMinutes] = useState('');
  const [closeFailureCategory, setCloseFailureCategory] = useState('');
  const [closeTechId, setCloseTechId] = useState('');
  
  // PM Checklist state
  const [pmChecklist, setPmChecklist] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistNotes, setChecklistNotes] = useState({});
  const [checklistSaving, setChecklistSaving] = useState(false);

  // Parts Used state
  const [partsUsed, setPartsUsed] = useState({ items: [], total_qty: 0, total_cost: 0 });
  const [availableParts, setAvailableParts] = useState([]);
  const [branchStock, setBranchStock] = useState([]);
  const [issuePart, setIssuePart] = useState({ part_id: '', qty: '' });
  const [issuingPart, setIssuingPart] = useState(false);

  // Reject Completion state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Re-open state (reason is mandatory)
  const [reopenReason, setReopenReason] = useState('');

  // Linked Work Request data (description, voice note, requester evidence)
  const [linkedWr, setLinkedWr] = useState(null);
  const [wrVoiceNoteUrl, setWrVoiceNoteUrl] = useState(null);
  const [wrAttachments, setWrAttachments] = useState([]);

  const STATUS_ACTIONS = {
    OPEN: [{ status: 'IN_PROGRESS', labelKey: 'workOrder.startWork', icon: Play, color: 'bg-brand hover:bg-brand-hover text-white' }],
    ASSIGNED: [{ status: 'IN_PROGRESS', labelKey: 'workOrder.startWork', icon: Play, color: 'bg-brand hover:bg-brand-hover text-white' }],
    IN_PROGRESS: [
      { status: 'WAITING_PARTS', labelKey: 'workOrder.waitingParts', icon: Pause, color: 'bg-amber-500 hover:bg-amber-600 text-white' },
      { status: 'WAITING_VENDOR', labelKey: 'workOrder.waitingVendor', icon: Pause, color: 'bg-orange-500 hover:bg-orange-600 text-white' },
      { status: 'COMPLETED', labelKey: 'workOrder.markComplete', icon: CheckCircle, color: 'bg-emerald-600 hover:bg-emerald-700 text-white', requiresConfirm: true },
    ],
    WAITING_PARTS: [{ status: 'IN_PROGRESS', labelKey: 'workOrder.resumeWork', icon: Play, color: 'bg-brand hover:bg-brand-hover text-white' }],
    WAITING_VENDOR: [{ status: 'IN_PROGRESS', labelKey: 'workOrder.resumeWork', icon: Play, color: 'bg-brand hover:bg-brand-hover text-white' }],
    COMPLETED: [{ status: 'CLOSED', labelKey: 'common.close', icon: Lock, color: 'bg-brand hover:bg-brand-hover text-white', requiresConfirm: true }],
    CLOSED: [{ status: 'OPEN', labelKey: 'workOrder.reopen', icon: RotateCcw, color: 'bg-amber-500 hover:bg-amber-600 text-white', requiresConfirm: true }],
  };

  useEffect(() => { loadAll(); }, [id]);

  const loadPartsData = async (branchId) => {
    try {
      const [partsRes, stockRes, usedRes] = await Promise.all([
        api.get('/inventory/parts', { params: { is_active: true, branch_id: branchId } }),
        api.get('/inventory/stock', { params: { branch_id: branchId } }),
        api.get(`/inventory/work-orders/${id}/parts`),
      ]);
      setAvailableParts(partsRes.data);
      setBranchStock(stockRes.data);
      setPartsUsed(usedRes.data);
    } catch (err) {
      console.log('Error loading parts data:', err);
    }
  };

  const loadTimeline = async () => {
    try {
      const { data } = await api.get(`/work-orders/${id}/timeline`);
      setTimeline(data);
    } catch (err) {
      console.log('Error loading timeline:', err);
    }
  };

  const loadAll = async () => {
    try {
      const [woRes, commRes] = await Promise.all([
        api.get(`/work-orders/${id}`),
        api.get(`/work-orders/${id}/comments`),
      ]);
      setWo(woRes.data);
      setComments(commRes.data);
      setResolution(woRes.data.resolution_text || '');
      setCosts({
        cost_labor: woRes.data.cost_labor || '',
        cost_parts: woRes.data.cost_parts || '',
        cost_vendor: woRes.data.cost_vendor || '',
      });
      
      // Load technicians for this branch
      if (['admin', 'supervisor', 'super_admin'].includes(user?.role)) {
        const techRes = await api.get('/users/technicians', { params: { branch_id: woRes.data.branch_id } });
        setTechnicians(techRes.data);
      }
      
      // Load parts data for this branch
      if (['admin', 'supervisor', 'super_admin', 'technician'].includes(user?.role)) {
        await loadPartsData(woRes.data.branch_id);
      }
      
      // Load timeline
      await loadTimeline();

      // Load linked Work Request data (description, voice note, evidence)
      if (woRes.data.work_request_id) {
        try {
          const [wrRes, wrAttRes] = await Promise.all([
            api.get(`/work-requests/${woRes.data.work_request_id}`),
            api.get(`/attachments?entity_type=work_request&entity_id=${woRes.data.work_request_id}`),
          ]);
          setLinkedWr(wrRes.data);
          setWrAttachments(wrAttRes.data);
          const audioAtt = wrAttRes.data.find(a => a.mime_type?.startsWith('audio/'));
          setWrVoiceNoteUrl(audioAtt?.download_url || null);
        } catch { /* WR may have been deleted */ }
      }
      
      // Load PM checklist for preventive WOs
      if (woRes.data.type === 'preventive') {
        try {
          const checklistRes = await api.get(`/pm/checklist/${id}`);
          setPmChecklist(checklistRes.data);
          const existingResult = checklistRes.data.result;
          if (existingResult?.items) {
            const checked = {};
            const notes = {};
            existingResult.items.forEach(item => {
              checked[item.item_id] = item.checked;
              if (item.note) notes[item.item_id] = item.note;
            });
            setChecklistItems(Object.keys(checked).filter(k => checked[k]));
            setChecklistNotes(notes);
          } else {
            setChecklistItems([]);
            setChecklistNotes({});
          }
        } catch (err) {
          console.log('No PM checklist for this WO');
        }
      }
    } catch (err) {
      toast.error(t('toast.error'));
      navigate('/work-orders');
    } finally { setLoading(false); }
  };

  const toggleChecklistItem = (itemId) => {
    setChecklistItems(prev => 
      prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
    );
  };

  const saveChecklist = async () => {
    if (!pmChecklist) return;
    setChecklistSaving(true);
    try {
      const items = pmChecklist.checklist_items.map(item => ({
        item_id: item.id,
        checked: checklistItems.includes(item.id),
        note: checklistNotes[item.id] || null,
      }));
      await api.post(`/pm/checklist/${id}`, { items });
      toast.success(t('pm.checklistSaved'));
      await loadTimeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setChecklistSaving(false);
    }
  };

  const issuePartToWO = async () => {
    if (!issuePart.part_id || !issuePart.qty || parseInt(issuePart.qty) <= 0) return;
    setIssuingPart(true);
    try {
      await api.post(`/inventory/work-orders/${id}/parts`, {
        part_id: issuePart.part_id,
        qty: parseInt(issuePart.qty),
      });
      toast.success(t('toast.saved'));
      setIssuePart({ part_id: '', qty: '' });
      await loadPartsData(wo.branch_id);
      const { data: refreshed } = await api.get(`/work-orders/${id}`);  // cost_parts is recalculated server-side from the ledger
      setWo(refreshed);
      setCosts(c => ({ ...c, cost_parts: refreshed.cost_parts ?? '' }));
      await loadTimeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setIssuingPart(false);
    }
  };

  const getPartStock = (partId) => {
    const stock = branchStock.find(s => s.part_id === partId);
    return stock ? stock.qty_on_hand : 0;
  };

  const assignTech = async () => {
    if (!selectedTech) return;
    setActionLoading('assign');
    try {
      await api.post(`/work-orders/${id}/assign`, { technician_id: selectedTech });
      toast.success(t('toast.saved'));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
    finally { setActionLoading(''); }
  };

  const handleActionClick = (action) => {
    if (action.requiresConfirm) {
      // Pre-calculate downtime for CLOSE action
      if (action.status === 'CLOSED' && wo.started_at && wo.completed_at) {
        const start = new Date(wo.started_at);
        const end = new Date(wo.completed_at);
        const minutes = Math.max(0, Math.round((end - start) / 60000));
        setCloseDowntimeMinutes(String(minutes));
      }
      setConfirmModal({ open: true, action, status: action.status });
    } else {
      changeStatus(action.status);
    }
  };

  const confirmAction = async () => {
    if (confirmModal.status) {
      // Re-open requires a reason
      if (confirmModal.status === 'OPEN' && wo.status === 'CLOSED' && !reopenReason.trim()) {
        toast.error(t('workOrder.reopenReasonRequired', 'A reason is required to re-open this work order'));
        return;
      }
      // If closing and a technician was selected in the modal, assign first
      if (confirmModal.status === 'CLOSED' && closeTechId && !wo.assigned_to) {
        try {
          await api.post(`/work-orders/${id}/assign`, { technician_id: closeTechId });
        } catch (err) {
          toast.error(err.response?.data?.detail || t('toast.error'));
          return;
        }
      }
      await changeStatus(confirmModal.status);
    }
    setConfirmModal({ open: false, action: null, status: null });
    // Reset close modal fields
    setCloseDowntimeMinutes('');
    setCloseFailureCategory('');
    setCloseTechId('');
    setReopenReason('');
  };

  const changeStatus = async (newStatus, resText = null) => {
    setActionLoading(newStatus);
    try {
      // COMPLETE: Check PM required items if preventive
      if (newStatus === 'COMPLETED' && wo.type === 'preventive' && pmChecklist) {
        const requiredItems = pmChecklist.checklist_items?.filter(i => i.is_required) || [];
        const uncheckedRequired = requiredItems.filter(i => !checklistItems.includes(i.id));
        if (uncheckedRequired.length > 0) {
          toast.error(t('pm.requiredItemsWarning'));
          setActionLoading('');
          return;
        }
      }
      
      // CLOSE: Save costs before status change (allow 0 values without confirmation)
      if (newStatus === 'CLOSED') {
        await api.put(`/work-orders/${id}`, {
          cost_labor: costs.cost_labor ? parseFloat(costs.cost_labor) : 0,
          cost_parts: costs.cost_parts ? parseFloat(costs.cost_parts) : 0,
          cost_vendor: costs.cost_vendor ? parseFloat(costs.cost_vendor) : 0,
        });
      }
      
      // Build status change payload
      const statusPayload = { 
        status: newStatus, 
        resolution_text: resText || resolution || null,
      };
      
      // Re-open requires a mandatory reason
      if (newStatus === 'OPEN' && wo.status === 'CLOSED') {
        statusPayload.reason = reopenReason.trim();
      }

      // Include optional downtime and failure category when closing
      if (newStatus === 'CLOSED') {        if (closeDowntimeMinutes && parseInt(closeDowntimeMinutes) >= 0) {
          statusPayload.downtime_minutes = parseInt(closeDowntimeMinutes);
        }
        if (closeFailureCategory) {
          statusPayload.failure_category = closeFailureCategory;
        }
      }
      
      await api.post(`/work-orders/${id}/status`, statusPayload);
      toast.success(t('toast.saved'));
      loadAll();
    } catch (err) { 
      toast.error(getErrorMessage(err, t('toast.error'), user)); 
    }
    finally { setActionLoading(''); }
  };

  const saveDetails = async () => {
    setActionLoading('details');
    try {
      await api.put(`/work-orders/${id}`, {
        resolution_text: resolution,
        cost_labor: costs.cost_labor ? parseFloat(costs.cost_labor) : null,
        cost_parts: costs.cost_parts ? parseFloat(costs.cost_parts) : null,
        cost_vendor: costs.cost_vendor ? parseFloat(costs.cost_vendor) : null,
      });
      toast.success(t('toast.saved'));
      loadAll();
    } catch (err) { toast.error(t('toast.error')); }
    finally { setActionLoading(''); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/work-orders/${id}/comments`, { text: newComment });
      setNewComment('');
      const commRes = await api.get(`/work-orders/${id}/comments`);
      setComments(commRes.data);
      await loadTimeline();
    } catch (err) { toast.error(t('toast.error')); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('assetDetail.copied'));
  };

  // Filter timeline items
  const filteredTimeline = useMemo(() => {
    if (timelineFilter === 'all') return timeline;
    const filterMap = {
      status: ['created', 'status_change', 'assigned', 'reopened'],
      comments: ['comment'],
      attachments: ['attachment'],
      parts: ['part_issued'],
      pm: ['checklist_updated'],
    };
    return timeline.filter(item => filterMap[timelineFilter]?.includes(item.type));
  }, [timeline, timelineFilter]);

  // Get icon for timeline item
  const getTimelineIcon = (type) => {
    const icons = {
      created: CircleDot,
      status_change: ChevronRight,
      assigned: User,
      reopened: RotateCcw,
      comment: MessageSquare,
      attachment: Paperclip,
      part_issued: Package,
      checklist_updated: ClipboardCheck,
    };
    return icons[type] || CircleDot;
  };

  // Get color for timeline item
  const getTimelineColor = (type) => {
    const colors = {
      created: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
      status_change: 'text-brand bg-brand/10',
      assigned: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
      reopened: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
      comment: 'text-muted-foreground bg-muted',
      attachment: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
      part_issued: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
      checklist_updated: 'text-teal-500 bg-teal-100 dark:bg-teal-900/30',
    };
    return colors[type] || 'text-muted-foreground bg-muted';
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  if (!wo) return null;

  const isManager = ['admin', 'supervisor', 'super_admin', 'branch_manager'].includes(user?.role);
  const canUpdate = ['admin', 'supervisor', 'super_admin', 'branch_manager', 'technician'].includes(user?.role);
  const canAssign = isManager && wo.status !== 'CLOSED' && !wo.assigned_to;
  const canReject = isManager && wo.status === 'COMPLETED';
  const canMessageTech = isManager && wo.assigned_to;
  const actions = STATUS_ACTIONS[wo.status] || [];

  const handleRejectCompletion = async () => {
    if (!rejectReason.trim()) {
      toast.error(t('workOrder.rejectReasonRequired'));
      return;
    }
    setRejectLoading(true);
    try {
      await api.post(`/work-orders/${id}/reject-completion`, { reason: rejectReason });
      toast.success(t('workOrder.completionRejected'));
      setShowRejectModal(false);
      setRejectReason('');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setRejectLoading(false);
    }
  };

  const handleMessageTechnician = async () => {
    if (!wo.assigned_to) return;
    try {
      toast.info(t('chat.startingChat'));
      const { data } = await api.post('/chat/threads', {
        type: 'private',
        title: `${t('chat.chatWith', { name: wo.assigned_to_name })} - WO#${wo.id.slice(0, 8)}`,
        participant_user_id: wo.assigned_to
      });
      navigate(`/chat?thread=${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    }
  };

  return (
    <div data-testid="wo-detail-page" className="space-y-6">
      {/* Header - At a Glance */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/work-orders')} className="shrink-0" data-testid="wo-back-btn">
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
        
        <div className="flex-1 space-y-3">
          {/* Title Row */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">
              {t('workOrder.title')}
            </h1>
            <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md">
              <span className="font-mono text-sm text-muted-foreground">#{wo.id.slice(0, 8)}</span>
              <button 
                onClick={() => copyToClipboard(wo.id)} 
                className="p-1 hover:bg-secondary rounded"
                title={t('assetDetail.copyId')}
              >
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
            <StatusBadge status={wo.status} />
            <SourceBadge source={wo.maintenance_source} />
            <Badge variant={wo.type === 'preventive' ? 'default' : 'outline'} 
                   className={wo.type === 'preventive' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 text-xs' : 'text-xs'}>
              {wo.type === 'preventive' && <Calendar className="w-3 h-3 me-1" />}
              {t(`workOrder.${wo.type}`)}
            </Badge>
          </div>
          
          {/* Info Chips Row */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <span className="font-medium text-foreground">{wo.branch_name}</span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground" data-testid="wo-detail-room">
              <span>•</span>
              <span>{wo.room_name || <span className="italic">{t('common.noRoom')}</span>}</span>
            </div>
            {wo.asset_name && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span>•</span>
                <span>{wo.asset_name}</span>
              </div>
            )}
            {wo.target_due_date && (
              <div className="flex items-center gap-1.5">
                <span>•</span>
                <Clock className="w-3.5 h-3.5" />
                <span className={wo.target_due_date < new Date().toISOString() && !['CLOSED', 'COMPLETED'].includes(wo.status) ? 'text-rose-500 font-medium' : 'text-muted-foreground'}>
                  {formatDate(wo.target_due_date)}
                </span>
              </div>
            )}
            {wo.assigned_to_name && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span>•</span>
                <User className="w-3.5 h-3.5" />
                <span>{wo.assigned_to_name}</span>
              </div>
            )}
          </div>
          
          {/* SLA Status Widget */}
          {wo.sla?.sla_policy_id && (
            <Card className="bg-muted/30 border-muted">
              <CardContent className="p-3">
                <SLAWidget sla={wo.sla} />
              </CardContent>
            </Card>
          )}
          
          {/* Source Links */}
          {(wo.work_request_title || wo.pm_plan_name) && (
            <div className="flex flex-wrap gap-2">
              {wo.work_request_title && (
                <span className="text-xs text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
                  {t('workOrder.fromRequest')}: {wo.work_request_title}
                </span>
              )}
              {wo.pm_plan_name && (
                <span className="text-xs text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                  PM: {wo.pm_plan_name}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions Dropdown */}
        {canUpdate && actions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="shrink-0" data-testid="wo-actions-dropdown">
                {t('common.actions')}
                <MoreVertical className="w-4 h-4 ms-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {actions.map((action, idx) => {
                const Icon = action.icon;
                return (
                  <DropdownMenuItem 
                    key={action.status}
                    onClick={() => handleActionClick(action)}
                    disabled={actionLoading === action.status}
                    className="cursor-pointer"
                    data-testid={`wo-action-${action.status.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4 me-2" />
                    {actionLoading === action.status ? t('common.loading') : t(action.labelKey)}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Reject Completion Button (manager only, COMPLETED status) */}
        {canReject && (
          <Button 
            variant="outline" 
            className="shrink-0 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
            onClick={() => setShowRejectModal(true)}
            data-testid="wo-reject-completion-btn"
          >
            <XCircle className="w-4 h-4 me-2" />
            {t('workOrder.rejectCompletion')}
          </Button>
        )}

        {/* Message Technician Button (manager only, when assigned) */}
        {canMessageTech && (
          <Button 
            variant="outline" 
            className="shrink-0"
            onClick={handleMessageTechnician}
            data-testid="wo-message-technician-btn"
          >
            <MessageCircle className="w-4 h-4 me-2" />
            {t('chat.messageTechnician')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column (2/3) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Linked Work Request Details (description, voice, evidence) */}
          {linkedWr && (
            <Card className="bg-card border-border shadow-sm" data-testid="wo-request-details">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground">Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Created By</p>
                    <p className="text-foreground mt-0.5">{linkedWr.created_by_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stage</p>
                    <p className="text-foreground mt-0.5">{wo.stage || linkedWr.stage || '—'}</p>
                  </div>
                </div>
                {linkedWr.description && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Description</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{linkedWr.description}</p>
                  </div>
                )}
                {wrVoiceNoteUrl && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Voice Note</p>
                    <VoicePlayer url={wrVoiceNoteUrl} />
                  </div>
                )}
                {wrAttachments.filter(a => !a.mime_type?.startsWith('audio/')).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Evidence Photos/Videos</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {wrAttachments.filter(a => !a.mime_type?.startsWith('audio/')).map(att => (
                        <a key={att.id} href={att.download_url} target="_blank" rel="noopener noreferrer" className="block rounded-lg border border-border overflow-hidden bg-muted/30 aspect-square">
                          {att.mime_type?.startsWith('image/') ? (
                            <img src={att.download_url} alt={att.filename} className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-2">
                              <FileText className="w-6 h-6" />
                              <span className="text-[10px] truncate max-w-full mt-1">{att.filename}</span>
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assign Technician (if OPEN) */}
          {canAssign && (
            <Card className="bg-card border-border shadow-sm border-amber-200 dark:border-amber-900/50">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[200px] space-y-1">
                    <Label className="text-sm flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-amber-500" />
                      {t('workOrder.assignTechnician')}
                    </Label>
                    <Select value={selectedTech} onValueChange={setSelectedTech}>
                      <SelectTrigger data-testid="wo-tech-select"><SelectValue placeholder={t('workOrder.selectTechnician')} /></SelectTrigger>
                      <SelectContent>
                        {technicians.map(tech => (
                          <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={assignTech} disabled={!selectedTech || actionLoading === 'assign'} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="wo-assign-btn">
                    {actionLoading === 'assign' ? t('common.loading') : t('workOrder.assign')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Closed Resolution Display */}
          {wo.status === 'CLOSED' && wo.resolution_text && (
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground">{t('workOrder.resolution')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{wo.resolution_text}</p>
                {(wo.cost_labor || wo.cost_parts || wo.cost_vendor) && (
                  <div className="mt-4 flex flex-wrap gap-6">
                    {wo.cost_labor > 0 && <div><span className="text-xs text-muted-foreground">{t('workOrder.laborCost')}:</span> <span className="text-sm font-medium text-foreground">{formatCurrency(wo.cost_labor)}</span></div>}
                    {wo.cost_parts > 0 && <div><span className="text-xs text-muted-foreground">{t('workOrder.partsCost')}:</span> <span className="text-sm font-medium text-foreground">{formatCurrency(wo.cost_parts)}</span></div>}
                    {wo.cost_vendor > 0 && <div><span className="text-xs text-muted-foreground">{t('workOrder.vendorCost')}:</span> <span className="text-sm font-medium text-foreground">{formatCurrency(wo.cost_vendor)}</span></div>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PM Checklist */}
          {wo.type === 'preventive' && pmChecklist && (
            <Card className="bg-card border-border shadow-sm" data-testid="pm-checklist-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                    {t('pm.checklist')}: {pmChecklist.plan_name}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {checklistItems.length}/{pmChecklist.checklist_items?.length || 0} {t('status.COMPLETED').toLowerCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {pmChecklist.checklist_items?.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2 text-center">{t('pm.noChecklist')}</p>
                )}
                {pmChecklist.checklist_items?.map((item, idx) => (
                  <div key={item.id} className={`p-3 rounded-lg border ${item.is_required && !checklistItems.includes(item.id) ? 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800' : 'border-border bg-muted/50'}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`checklist-${item.id}`}
                        checked={checklistItems.includes(item.id)}
                        onCheckedChange={() => toggleChecklistItem(item.id)}
                        disabled={wo.status === 'CLOSED'}
                        className="mt-0.5"
                        data-testid={`pm-checklist-item-${idx}`}
                      />
                      <div className="flex-1">
                        <Label htmlFor={`checklist-${item.id}`} className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                          {item.text}
                          {item.is_required && (
                            <Badge variant="destructive" className="text-xs py-0 px-1.5 h-4">{t('common.required')}</Badge>
                          )}
                        </Label>
                        {wo.status !== 'CLOSED' && (
                          <Input
                            placeholder={t('pm.addNote')}
                            value={checklistNotes[item.id] || ''}
                            onChange={(e) => setChecklistNotes({ ...checklistNotes, [item.id]: e.target.value })}
                            className="mt-2 text-xs h-8"
                            data-testid={`pm-checklist-note-${idx}`}
                          />
                        )}
                        {wo.status === 'CLOSED' && checklistNotes[item.id] && (
                          <p className="text-xs text-muted-foreground mt-1 italic">{checklistNotes[item.id]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {pmChecklist.checklist_items?.some(item => item.is_required && !checklistItems.includes(item.id)) && wo.status !== 'CLOSED' && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{t('pm.requiredItemsWarning')}</span>
                  </div>
                )}
                
                {canUpdate && wo.status !== 'CLOSED' && (
                  <Button size="sm" onClick={saveChecklist} disabled={checklistSaving} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="pm-checklist-save-btn">
                    <ClipboardCheck className="w-3.5 h-3.5 me-1" />
                    {checklistSaving ? t('pm.savingChecklist') : t('pm.saveChecklist')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Parts Used Section */}
          {['admin', 'supervisor', 'super_admin', 'technician'].includes(user?.role) && (
            <Card className="bg-card border-border shadow-sm" data-testid="parts-used-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                    <Package className="w-5 h-5 text-purple-600" />
                    {t('inventory.partsUsed')}
                  </CardTitle>
                  {partsUsed.total_qty > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {partsUsed.total_qty} {t('inventory.totalParts')} • {formatCurrency(partsUsed.total_cost)} {t('inventory.totalCost')}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {canUpdate && wo.status !== 'CLOSED' && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[200px] space-y-1">
                        <Label className="text-xs">{t('inventory.selectPart')}</Label>
                        <Select value={issuePart.part_id} onValueChange={(v) => setIssuePart({ ...issuePart, part_id: v })}>
                          <SelectTrigger data-testid="issue-part-select">
                            <SelectValue placeholder={t('inventory.selectPart')} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableParts.map(p => {
                              const stock = getPartStock(p.id);
                              return (
                                <SelectItem key={p.id} value={p.id} disabled={stock <= 0}>
                                  {p.name} ({p.sku || 'N/A'}) — {stock} {p.unit} {t('inventory.qtyOnHand').toLowerCase()}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-24 space-y-1">
                        <Label className="text-xs">{t('inventory.qty')}</Label>
                        <Input
                          type="number"
                          min="1"
                          max={issuePart.part_id ? getPartStock(issuePart.part_id) : 9999}
                          value={issuePart.qty}
                          onChange={(e) => setIssuePart({ ...issuePart, qty: e.target.value })}
                          placeholder="0"
                          data-testid="issue-part-qty"
                        />
                      </div>
                      <Button 
                        size="sm" 
                        onClick={issuePartToWO} 
                        disabled={issuingPart || !issuePart.part_id || !issuePart.qty}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="issue-part-btn"
                      >
                        <Plus className="w-4 h-4 me-1" />
                        {issuingPart ? t('common.loading') : t('inventory.addPart')}
                      </Button>
                    </div>
                    {issuePart.part_id && getPartStock(issuePart.part_id) <= 0 && (
                      <p className="text-xs text-rose-500 mt-2">{t('inventory.insufficientStock')}</p>
                    )}
                  </div>
                )}

                {partsUsed.items.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t('inventory.noPartsUsed')}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>{t('inventory.partName')}</TableHead>
                        <TableHead>{t('common.type')}</TableHead>
                        <TableHead>{t('inventory.qty')}</TableHead>
                        <TableHead>{t('inventory.unitCost')}</TableHead>
                        <TableHead className="text-end">{t('inventory.totalCost')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partsUsed.items.map((item, idx) => (
                        <TableRow key={item.id} data-testid={`parts-used-row-${idx}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-foreground">{item.part_name}</p>
                              <p className="text-xs text-muted-foreground">{item.part_sku || 'N/A'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{item.movement_type}</TableCell>
                          <TableCell>
                            <span className="font-medium">{item.qty}</span>
                            <span className="text-muted-foreground text-xs ms-1">{item.part_unit}</span>
                          </TableCell>
                          <TableCell data-testid={`parts-used-unit-cost-${idx}`}>
                            {item.unit_cost != null ? formatCurrency(item.unit_cost) : (
                              <span className="text-xs">{t('inventory.legacy', 'legacy')}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-end font-medium" data-testid={`parts-used-line-cost-${idx}`}>
                            {item.line_cost != null ? formatCurrency(item.line_cost) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-medium">
                        <TableCell>{t('common.all')}</TableCell>
                        <TableCell></TableCell>
                        <TableCell>{partsUsed.total_qty}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-end" data-testid="parts-used-total-cost">{formatCurrency(partsUsed.total_cost)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resolution & Costs (editable when not closed) */}
          {wo.status !== 'CLOSED' && canUpdate && (
            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  {t('workOrder.resolutionCosts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('workOrder.resolutionText')}</Label>
                  <Textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder={t('workOrder.describeWork')}
                    rows={3}
                    data-testid="wo-resolution-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('workOrder.laborCost')} ({t('common.currency')})</Label>
                    <Input
                      type="number"
                      value={costs.cost_labor}
                      onChange={(e) => setCosts(c => ({ ...c, cost_labor: e.target.value }))}
                      placeholder="0"
                      data-testid="wo-cost-labor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('workOrder.partsCost')} ({t('common.currency')})</Label>
                    <Input
                      type="number"
                      value={partsUsed.items.length > 0 ? (wo.cost_parts ?? 0) : costs.cost_parts}
                      onChange={(e) => setCosts(c => ({ ...c, cost_parts: e.target.value }))}
                      placeholder="0"
                      readOnly={partsUsed.items.length > 0}
                      className={partsUsed.items.length > 0 ? 'bg-muted cursor-not-allowed' : ''}
                      data-testid="wo-cost-parts-input"
                    />
                    {partsUsed.items.length > 0 && (
                      <p className="text-xs text-muted-foreground" data-testid="wo-cost-parts-derived-hint">
                        {t('inventory.costFromLedger', 'Calculated from issued parts')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('workOrder.vendorCost')} ({t('common.currency')})</Label>
                    <Input
                      type="number"
                      value={costs.cost_vendor}
                      onChange={(e) => setCosts(c => ({ ...c, cost_vendor: e.target.value }))}
                      placeholder="0"
                      data-testid="wo-cost-vendor-input"
                    />
                  </div>
                </div>
                <Button size="sm" onClick={saveDetails} disabled={actionLoading === 'details'} data-testid="wo-save-details-btn">
                  {actionLoading === 'details' ? t('common.loading') : t('workOrder.saveDetails')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
                {t('comments.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments.length === 0 && <p className="text-muted-foreground text-sm py-4 text-center">{t('comments.noComments')}</p>}
              {comments.map(c => (
                <div key={c.id} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-foreground">{c.author_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(c.created_at)}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{c.text}</p>
                </div>
              ))}
              {canUpdate && (
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('comments.addComment')}
                    onKeyDown={(e) => e.key === 'Enter' && addComment()}
                    data-testid="wo-comment-input"
                  />
                  <Button size="icon" onClick={addComment} data-testid="wo-comment-submit-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments */}
          <FileUpload
            entityType="work_order"
            entityId={wo.id}
            branchId={wo.branch_id}
            canUpload={canUpdate && wo.status !== 'CLOSED'}
          />
        </div>

        {/* Sidebar - Timeline (1/3) */}
        <div className="space-y-6">
          {/* Timeline Panel */}
          <Card className="bg-card border-border shadow-sm" data-testid="timeline-panel">
            <CardHeader className="pb-2">
              <CardTitle className="font-barlow text-lg text-foreground">{t('timeline.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Timeline Filters */}
              <div className="px-4 pb-3 border-b border-border">
                <Tabs value={timelineFilter} onValueChange={setTimelineFilter} className="w-full">
                  <TabsList className="w-full grid grid-cols-3 h-8">
                    <TabsTrigger value="all" className="text-xs">{t('timeline.filterAll')}</TabsTrigger>
                    <TabsTrigger value="status" className="text-xs">{t('timeline.filterStatus')}</TabsTrigger>
                    <TabsTrigger value="comments" className="text-xs">{t('timeline.filterComments')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="flex gap-1 mt-2">
                  <Button 
                    variant={timelineFilter === 'attachments' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    className="h-6 text-xs px-2"
                    onClick={() => setTimelineFilter(timelineFilter === 'attachments' ? 'all' : 'attachments')}
                  >
                    <Paperclip className="w-3 h-3 me-1" />
                    {t('timeline.filterAttachments')}
                  </Button>
                  <Button 
                    variant={timelineFilter === 'parts' ? 'secondary' : 'ghost'} 
                    size="sm" 
                    className="h-6 text-xs px-2"
                    onClick={() => setTimelineFilter(timelineFilter === 'parts' ? 'all' : 'parts')}
                  >
                    <Package className="w-3 h-3 me-1" />
                    {t('timeline.filterParts')}
                  </Button>
                  {wo.type === 'preventive' && (
                    <Button 
                      variant={timelineFilter === 'pm' ? 'secondary' : 'ghost'} 
                      size="sm" 
                      className="h-6 text-xs px-2"
                      onClick={() => setTimelineFilter(timelineFilter === 'pm' ? 'all' : 'pm')}
                    >
                      <ClipboardCheck className="w-3 h-3 me-1" />
                      {t('timeline.filterPM')}
                    </Button>
                  )}
                </div>
              </div>

              {/* Timeline Items */}
              <div className="max-h-[500px] overflow-y-auto">
                {filteredTimeline.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">{t('timeline.noActivity')}</div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className={`absolute ${isRTL ? 'right-6' : 'left-6'} top-0 bottom-0 w-0.5 bg-border`} />
                    
                    {filteredTimeline.map((item, idx) => {
                      const Icon = getTimelineIcon(item.type);
                      const colorClass = getTimelineColor(item.type);
                      return (
                        <div key={idx} className="relative px-4 py-3 hover:bg-muted/30 transition-colors">
                          <div className="flex gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${colorClass}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground">
                                {item.type === 'created' && t('timeline.created')}
                                {item.type === 'status_change' && `${t('timeline.statusChange')}: ${item.details?.from_status} → ${item.details?.to_status}`}
                                {item.type === 'assigned' && `${t('timeline.assigned')}: ${item.details?.technician_name}`}
                                {item.type === 'reopened' && `${t('workOrder.reopen')}: ${item.details?.reason || '—'}`}
                                {item.type === 'comment' && t('timeline.comment')}
                                {item.type === 'attachment' && t('timeline.attachment')}
                                {item.type === 'part_issued' && `${t('timeline.partIssued')}: ${item.details?.part_name} (${item.details?.qty})`}
                                {item.type === 'checklist_updated' && `${t('timeline.checklistUpdated')} (${item.details?.items_completed})`}
                              </p>
                              {item.details?.text && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.details.text}</p>
                              )}
                              {item.details?.filename && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.details.filename}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {item.user_name} • {formatDate(item.timestamp)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground">{t('workOrder.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.created')}</span>
                  <span className="text-foreground">{formatDate(wo.created_at)}</span>
                </div>
                {wo.started_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workOrder.started')}</span>
                    <span className="text-foreground">{formatDate(wo.started_at)}</span>
                  </div>
                )}
                {wo.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workOrder.completed')}</span>
                    <span className="text-foreground">{formatDate(wo.completed_at)}</span>
                  </div>
                )}
                {wo.status === 'CLOSED' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('workOrder.closed')}</span>
                    <span className="text-foreground">{formatDate(wo.closed_at || wo.updated_at)}</span>
                  </div>
                )}
                {(wo.duration_minutes !== null && wo.duration_minutes !== undefined) && (
                  <div className="flex justify-between border-t border-border pt-3">
                    <span className="text-muted-foreground">{t('workOrder.duration', 'Duration')}</span>
                    <span className="text-foreground font-semibold" data-testid="wo-duration-value">{wo.duration_minutes} min</span>
                  </div>
                )}
                {wo.reopened_at && (
                  <div className="border-t border-border pt-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('workOrder.reopen')}</span>
                      <span className="text-amber-600 dark:text-amber-400">{formatDate(wo.reopened_at)}</span>
                    </div>
                    {wo.reopen_reason && (
                      <p className="text-xs text-muted-foreground" data-testid="wo-reopen-reason">{wo.reopen_reason}</p>
                    )}
                    {wo.reopen_count > 1 && (
                      <p className="text-xs text-muted-foreground">Re-opened {wo.reopen_count} times</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action Confirmation Modal */}
      <Dialog open={confirmModal.open} onOpenChange={(open) => {
        if (!open) {
          setConfirmModal({ open: false, action: null, status: null });
          setCloseDowntimeMinutes('');
          setCloseFailureCategory('');
          setCloseTechId('');
          setReopenReason('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>
              {confirmModal.status === 'COMPLETED' && t('workOrder.confirmComplete', 'Are you sure you want to mark this work order as complete?')}
              {confirmModal.status === 'CLOSED' && t('workOrder.confirmClose', 'Are you sure you want to close this work order?')}
              {confirmModal.status === 'OPEN' && t('workOrder.confirmReopen')}
            </DialogDescription>
          </DialogHeader>
          
          {/* Mandatory reason for RE-OPEN action */}
          {confirmModal.status === 'OPEN' && wo.status === 'CLOSED' && (
            <div className="space-y-2 py-2">
              <Label className="text-sm font-medium">{t('workOrder.reopenReason', 'Reason for re-opening')} *</Label>
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder={t('workOrder.reopenReasonPlaceholder', 'Explain why this work order must be re-opened')}
                rows={3}
                data-testid="reopen-reason-input"
              />
            </div>
          )}

          {/* Optional fields for CLOSE action */}
          {confirmModal.status === 'CLOSED' && (
            <div className="space-y-4 py-4">
              {/* Technician selector when no tech assigned */}
              {!wo.assigned_to && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-600 dark:text-amber-400">{t('workOrder.assignTechnician')} *</Label>
                  <Select value={closeTechId} onValueChange={setCloseTechId}>
                    <SelectTrigger data-testid="close-tech-select">
                      <SelectValue placeholder={t('workOrder.selectTechnician')} />
                    </SelectTrigger>
                    <SelectContent>
                      {technicians.map(tech => (
                        <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {t('workOrder.techRequiredForClose', 'A technician must be assigned before closing for proper reporting.')}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="downtime">{t('downtime.minutes')}</Label>
                <Input
                  id="downtime"
                  type="number"
                  min="0"
                  value={closeDowntimeMinutes}
                  readOnly
                  className="bg-muted/50"
                  data-testid="close-downtime-input"
                />
                <p className="text-xs text-muted-foreground">
                  {wo.started_at && wo.completed_at
                    ? 'Auto-calculated from Start Work → Submit for Review'
                    : 'No timing data available (work was not started via the system)'}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="failureCategory">{t('failureCategory.title')}</Label>
                <Select value={closeFailureCategory} onValueChange={setCloseFailureCategory}>
                  <SelectTrigger data-testid="close-failure-category-select">
                    <SelectValue placeholder={t('common.selectOption')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electrical">{t('failureCategory.electrical')}</SelectItem>
                    <SelectItem value="mechanical">{t('failureCategory.mechanical')}</SelectItem>
                    <SelectItem value="software">{t('failureCategory.software')}</SelectItem>
                    <SelectItem value="network">{t('failureCategory.network')}</SelectItem>
                    <SelectItem value="sensor">{t('failureCategory.sensor')}</SelectItem>
                    <SelectItem value="audio">{t('failureCategory.audio')}</SelectItem>
                    <SelectItem value="lighting">{t('failureCategory.lighting')}</SelectItem>
                    <SelectItem value="controller_plc">{t('failureCategory.controller_plc')}</SelectItem>
                    <SelectItem value="wear_tear">{t('failureCategory.wear_tear')}</SelectItem>
                    <SelectItem value="user_damage">{t('failureCategory.user_damage')}</SelectItem>
                    <SelectItem value="consumable">{t('failureCategory.consumable')}</SelectItem>
                    <SelectItem value="unknown">{t('failureCategory.unknown')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setConfirmModal({ open: false, action: null, status: null });
              setCloseDowntimeMinutes('');
              setCloseFailureCategory('');
              setCloseTechId('');
              setReopenReason('');
            }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmAction}
              disabled={
                (confirmModal.status === 'CLOSED' && !wo.assigned_to && !closeTechId) ||
                (confirmModal.status === 'OPEN' && wo.status === 'CLOSED' && !reopenReason.trim())
              }
              className="bg-brand hover:bg-brand-hover text-brand-foreground"
              data-testid="confirm-action-btn"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Completion Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-barlow text-red-600">{t('workOrder.rejectCompletion')}</DialogTitle>
            <DialogDescription>{t('workOrder.rejectCompletionDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium">{t('workOrder.rejectReason')} *</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('workOrder.rejectReasonPlaceholder')}
              className="mt-1"
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRejectCompletion} 
              disabled={rejectLoading || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {rejectLoading ? t('common.loading') : t('workOrder.rejectCompletion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
